import ToggleInput from "@components/inputs/ToggleInput";
import { primeServices } from "@services/apis/Prime";
import { BrPixStatusItem } from "@services/models/Prime/BrPixStatusModel";
import { NtxBrlRatesData } from "@services/models/Prime/NtxBrlRatesModel";
import webSocketService from "@services/websocket/WebsocketServices";
import { useCallback, useEffect, useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const formatServiceShortLabel = (service: string) => {
  if (service === "cashIn") return "In";
  if (service === "cashOut") return "Out";
  return service;
};

const formatRate = (value?: number) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "--";
  return (1 / num).toFixed(4);
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

interface BrPixServiceStatusProps {
  onOpenRates: () => void;
  ratesVersion?: number;
}

const BrPixServiceStatus = ({
  onOpenRates,
  ratesVersion = 0,
}: BrPixServiceStatusProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  const [services, setServices] = useState<BrPixStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [rates, setRates] = useState<NtxBrlRatesData | null>(null);

  const handleServiceStatusUpdate = useCallback((payload: BrPixStatusItem) => {
    if (!payload?.providerId || !payload?.service) return;

    setServices((prev) => {
      const exists = prev.some(
        (entry) =>
          entry.providerId === payload.providerId &&
          entry.service === payload.service,
      );

      if (!exists) {
        return [...prev, payload];
      }

      return prev.map((entry) =>
        entry.providerId === payload.providerId &&
        entry.service === payload.service
          ? {
              ...entry,
              status: payload.status,
              active: payload.active,
            }
          : entry,
      );
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStatuses = async () => {
      try {
        setLoading(true);
        const [statusResult, ratesResult] = await Promise.allSettled([
          primeServices.getBrPixStatus(),
          primeServices.getNtxBrlRates(),
        ]);

        if (cancelled) return;

        if (statusResult.status === "fulfilled") {
          setServices(statusResult.value.data || []);
        } else {
          handleErrorRef.current({
            message: getApiErrorMessage(
              statusResult.reason,
              "Failed to fetch BRL service status",
            ),
          });
        }

        if (ratesResult.status === "fulfilled") {
          setRates(ratesResult.value.data);
        } else {
          handleErrorRef.current({
            message: getApiErrorMessage(
              ratesResult.reason,
              "Failed to fetch BRL NTX rates",
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStatuses();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (ratesVersion === 0) return;

    let cancelled = false;
    const refreshRates = async () => {
      try {
        const response = await primeServices.getNtxBrlRates();
        if (!cancelled) {
          setRates(response.data);
        }
      } catch {
        // Chip can stay on last known rates.
      }
    };

    refreshRates();
    return () => {
      cancelled = true;
    };
  }, [ratesVersion]);

  useEffect(() => {
    try {
      webSocketService.protectedSocket?.off("admin_brl_service_update");
      webSocketService.onMessage(
        "admin_brl_service_update",
        true,
        handleServiceStatusUpdate,
      );
    } catch (error) {
      console.error("WebSocket initialization error:", error);
    }

    return () => {
      webSocketService.offMessage(
        "admin_brl_service_update",
        true,
        handleServiceStatusUpdate,
      );
    };
  }, [handleServiceStatusUpdate, webSocketService.protectedSocket?.connected]);

  const handleToggle = async (item: BrPixStatusItem) => {
    const key = `${item.providerId}-${item.service}`;
    const nextStatus = item.active ? "disable" : "enable";

    setUpdatingKey(key);
    try {
      const response = await primeServices.updateBrPixStatus({
        providerId: item.providerId,
        service: item.service,
        status: nextStatus,
      });
      handleSuccess({
        message: response.msg || "BRL service status updated successfully",
      });
      setServices((prev) =>
        prev.map((entry) =>
          entry.providerId === item.providerId && entry.service === item.service
            ? response.data
            : entry,
        ),
      );
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg || "Failed to update BRL service status",
      });
    } finally {
      setUpdatingKey(null);
    }
  };

  if (loading) {
    return (
      <span className="text-text-subtle text-[11px] whitespace-nowrap">
        Loading BRL...
      </span>
    );
  }

  const providers = [...new Set(services.map((item) => item.providerId))];

  return (
    <div className="flex items-center gap-2 shrink-0">
      {services.length > 0 && (
        <>
          <span className="text-text-subtle text-[11px] font-semibold whitespace-nowrap">
            BRL Maint.
          </span>
          {providers.map((providerId) => (
            <div
              key={providerId}
              className="flex items-center gap-1.5 border border-border-subtle rounded-md px-2 py-1 bg-bg-page-body"
            >
              <span className="text-text-base text-[11px] font-semibold uppercase">
                {providerId}
              </span>
              {services
                .filter((item) => item.providerId === providerId)
                .map((item) => {
                  const key = `${item.providerId}-${item.service}`;
                  const isUpdating = updatingKey === key;
                  return (
                    <div key={key} className="flex items-center gap-1">
                      <span
                        className={`text-[10px] font-medium ${
                          item.active
                            ? "text-text-status-green"
                            : "text-text-status-red"
                        }`}
                      >
                        {formatServiceShortLabel(item.service)}
                      </span>
                      <div
                        className={
                          isUpdating ? "opacity-50 pointer-events-none" : ""
                        }
                      >
                        <ToggleInput
                          isToggled={item.active}
                          onToggle={() => handleToggle(item)}
                          size="sm"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </>
      )}

      <span className="text-text-subtle text-[11px] font-semibold whitespace-nowrap">
        BRL Rates
      </span>
      <button
        type="button"
        onClick={onOpenRates}
        className="flex items-center gap-1.5 border border-border-subtle rounded-md px-2 py-1 bg-bg-page-body hover:border-border-button-secondary"
        title="View and edit BRL NTX spreads"
      >
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-medium text-text-status-green">
            In
          </span>
          <span className="text-text-base text-[11px] font-semibold">
            {formatRate(rates?.payIn.effectiveRate)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-medium text-text-status-green">
            Out
          </span>
          <span className="text-text-base text-[11px] font-semibold">
            {formatRate(rates?.payOut.effectiveRate)}
          </span>
        </div>
      </button>
    </div>
  );
};

export default BrPixServiceStatus;
